import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, FolderArchive, BookOpen, Archive } from "lucide-react";
import { apiFetch, API } from "@/utils/api";
import GestoraExportDialog from "@/components/GestoraExportDialog";
import TaxDraftsSection from "@/components/TaxDraftsSection";

function ExportCard({ title, description, icon: Icon, onExport, loading }) {
  return (
    <Card className="card-hover">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-slate-100 rounded-lg">
            <Icon className="w-6 h-6 text-slate-600" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900">{title}</h3>
            <p className="text-sm text-slate-500 mt-1">{description}</p>
          </div>
          <Button onClick={onExport} disabled={loading} size="sm" data-testid={`export-${title.toLowerCase().replace(/\s+/g, '-')}`}>
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Exports() {
  const [startDate, setStartDate] = useState(
    new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10)
  );
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState({});
  const [gestoriaYear, setGestoriaYear] = useState(new Date().getFullYear());
  const [gestoriaQuarter, setGestoriaQuarter] = useState(
    Math.ceil((new Date().getMonth() + 1) / 3)
  );
  const [exportFormat, setExportFormat] = useState("csv");
  const [gestoriaLoading, setGestoriaLoading] = useState(false);
  const [gestoraDialogOpen, setGestoraDialogOpen] = useState(false);
  // Enlace "ver borrador" desde el cierre de trimestre (roadmap 2.5 bloque 4)
  const [draftRequest, setDraftRequest] = useState(null);

  const handleGestoriaPack = async () => {
    setGestoriaLoading(true);
    try {
      const response = await apiFetch(
        `${API}/export/gestoria-pack?year=${gestoriaYear}&quarter=${gestoriaQuarter}`
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `gestoria_${gestoriaYear}_T${gestoriaQuarter}.zip`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("Pack gestoría descargado");
      } else {
        const err = await response.json();
        toast.error(err.detail || "Error al generar el pack");
      }
    } catch (error) {
      toast.error("Error al descargar el pack gestoría");
    } finally {
      setGestoriaLoading(false);
    }
  };

  const handleGestoraExcel = () => setGestoraDialogOpen(true);

  const handleExport = async (type, filename) => {
    setLoading((prev) => ({ ...prev, [type]: true }));
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        format: exportFormat,
      });

      const response = await apiFetch(`${API}/export/${type}?${params}`);

      if (response.ok) {
        const blob = await response.blob();
        const ext = exportFormat === "xlsx" ? "xlsx" : "csv";
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filename}_${startDate}_${endDate}.${ext}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Exportación completada");
      } else {
        toast.error("Error en la exportación");
      }
    } catch (error) {
      toast.error("Error en la exportación");
    } finally {
      setLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="exports-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          Exportar Datos
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Exporta datos para tu gestoría o análisis externo
        </p>
      </div>

      {/* Date Range & Format */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Rango de fechas y formato</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-48 max-w-full">
              <Label className="mb-2 block">Desde</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="export-start-date"
              />
            </div>
            <div className="w-48 max-w-full">
              <Label className="mb-2 block">Hasta</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="export-end-date"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const now = new Date();
                  setStartDate(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
                  setEndDate(now.toISOString().slice(0, 10));
                }}
              >
                Este mes
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const now = new Date();
                  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                  const endLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
                  setStartDate(lastMonth.toISOString().slice(0, 10));
                  setEndDate(endLastMonth.toISOString().slice(0, 10));
                }}
              >
                Mes anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const now = new Date();
                  setStartDate(new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10));
                  setEndDate(now.toISOString().slice(0, 10));
                }}
              >
                Este año
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Label>Formato:</Label>
            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg" data-testid="export-format-selector">
              <Button
                variant={exportFormat === "csv" ? "default" : "ghost"}
                size="sm"
                className="h-8 px-3"
                onClick={() => setExportFormat("csv")}
              >
                <FileText className="w-4 h-4 mr-1.5" />
                CSV
              </Button>
              <Button
                variant={exportFormat === "xlsx" ? "default" : "ghost"}
                size="sm"
                className="h-8 px-3"
                onClick={() => setExportFormat("xlsx")}
              >
                <FileSpreadsheet className="w-4 h-4 mr-1.5" />
                Excel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Options */}
      <div className="grid md:grid-cols-2 gap-4">
        <ExportCard
          title="Facturas Recibidas"
          description="Exporta todas las facturas de compras y gastos"
          icon={FileText}
          onExport={() => handleExport("invoices-received", "facturas_recibidas")}
          loading={loading["invoices-received"]}
        />

        <ExportCard
          title="Facturas Emitidas"
          description="Exporta todas las facturas de ventas"
          icon={FileSpreadsheet}
          onExport={() => handleExport("invoices-emitted", "facturas_emitidas")}
          loading={loading["invoices-emitted"]}
        />

        <ExportCard
          title="Movimientos Bancarios"
          description="Exporta los movimientos bancarios clasificados"
          icon={FileSpreadsheet}
          onExport={() => handleExport("bank-transactions", "movimientos_banco")}
          loading={loading["bank-transactions"]}
        />

        <ExportCard
          title="Libro Diario"
          description="Exporta todos los asientos contables del período"
          icon={BookOpen}
          onExport={() => handleExport("journal", "libro_diario")}
          loading={loading["journal"]}
        />
      </div>

      {/* Pack Gestoria */}
      <Card data-testid="gestoria-pack-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Archive className="w-5 h-5 text-slate-600" />
            Cierre de trimestre
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-4">
            Export del trimestre: el Excel con las facturas emitidas y recibidas
            agrupadas para presentar los impuestos (tú o tu gestoría/asesoría), y
            un ZIP con todos los PDFs adjuntos.
          </p>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-32">
              <Label className="mb-2 block">Año</Label>
              <Select
                value={String(gestoriaYear)}
                onValueChange={(v) => setGestoriaYear(Number(v))}
              >
                <SelectTrigger data-testid="gestoria-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[0, 1, 2].map((offset) => {
                    const y = new Date().getFullYear() - offset;
                    return (
                      <SelectItem key={y} value={String(y)}>
                        {y}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="w-32">
              <Label className="mb-2 block">Trimestre</Label>
              <Select
                value={String(gestoriaQuarter)}
                onValueChange={(v) => setGestoriaQuarter(Number(v))}
              >
                <SelectTrigger data-testid="gestoria-quarter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">T1 (Ene-Mar)</SelectItem>
                  <SelectItem value="2">T2 (Abr-Jun)</SelectItem>
                  <SelectItem value="3">T3 (Jul-Sep)</SelectItem>
                  <SelectItem value="4">T4 (Oct-Dic)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleGestoriaPack}
              disabled={gestoriaLoading}
              data-testid="gestoria-download-btn"
            >
              {gestoriaLoading ? (
                <Download className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Descargar ZIP
            </Button>
            <Button
              onClick={handleGestoraExcel}
              variant="outline"
              data-testid="gestora-excel-download-btn"
            >
              Excel del trimestre (4 hojas)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Borradores AEAT informativos (roadmap 2.5) */}
      <TaxDraftsSection request={draftRequest} />

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">
                Formatos de exportación
              </p>
              <p className="text-sm text-blue-700 mt-1">
                <strong>CSV:</strong> Separador punto y coma (;), compatible con Excel en español. Los importes usan punto decimal.
              </p>
              <p className="text-sm text-blue-700 mt-1">
                <strong>Excel:</strong> Formato .xlsx nativo, con cabeceras en negrita y columnas auto-ajustadas.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <GestoraExportDialog
        open={gestoraDialogOpen}
        onOpenChange={setGestoraDialogOpen}
        year={gestoriaYear}
        quarter={gestoriaQuarter}
        onOpenDraft={(y, q) => setDraftRequest({ year: y, quarter: q, ts: Date.now() })}
      />
    </div>
  );
}
